import { FlexRow } from "@/components/flex-row";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from "@/components/ui/item";
import { Progress } from "@/components/ui/progress";
import { AbsoluteDate } from "@/components/utils/date-utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  CarIcon,
  ClockIcon,
  SquarePenIcon,
  Trash2Icon,
  UserIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

function DraftSummaryCard({
  name,
  kvps,
  vehiclePrimaryLine,
  vehicleModelLine,
  vehicleVinLine,
  client,
  clientUnset = false,
  lastModified,
  daysRemaining,
  expiringSoon = false,
  currentStep,
  steps = 8,
  author,
  createdAt,
  isLockedByOther = false,
  editorName,
  onEdit,
  onDelete,
}: {
  name: string;
  kvps: string;
  vehiclePrimaryLine: string;
  vehicleModelLine: string | null;
  vehicleVinLine: string | null;
  client: string;
  clientUnset?: boolean;
  lastModified: Date;
  daysRemaining: number;
  expiringSoon?: boolean;
  currentStep: number;
  steps?: number;
  author: string;
  createdAt: Date;
  isLockedByOther?: boolean;
  editorName?: string | null;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const expiryClassName =
    daysRemaining <= 1 ? "text-destructive font-medium" : undefined;

  return (
    <Card className={cn(expiringSoon && "border-amber-300 bg-amber-50/40")}>
      <CardHeader>
        <div className="flex flex-row flex-wrap items-center gap-2">
          <span className="font-bold">{name}</span>
          <Badge className="font-bold" variant="outline">
            KVPS : {kvps}
          </Badge>
          {expiringSoon ? (
            <Badge
              variant="outline"
              className="border-amber-400 text-amber-800"
            >
              Expire bientôt
            </Badge>
          ) : null}
          {isLockedByOther ? (
            <Badge variant="secondary">
              En cours d&apos;édition{editorName ? ` par ${editorName}` : ""}
            </Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-4 items-start gap-4">
          <Item>
            <ItemMedia>
              <CarIcon className="text-primary" />
            </ItemMedia>
            <ItemContent>
              <span className="text-sm text-gray-600">Véhicule</span>
              <ItemTitle>{vehiclePrimaryLine}</ItemTitle>
              {vehicleModelLine ? (
                <ItemDescription>{vehicleModelLine}</ItemDescription>
              ) : (
                <ItemDescription className="text-muted-foreground italic">
                  Modèle non disponible
                </ItemDescription>
              )}
              {vehicleVinLine ? (
                <ItemDescription>{vehicleVinLine}</ItemDescription>
              ) : null}
            </ItemContent>
          </Item>

          <Item>
            <ItemMedia>
              <UserIcon className="text-primary" />
            </ItemMedia>
            <ItemContent>
              <span className="text-sm text-gray-600">Client</span>
              <ItemTitle
                className={cn(clientUnset && "text-muted-foreground italic")}
              >
                {client}
              </ItemTitle>
              <ItemDescription>&nbsp;</ItemDescription>
            </ItemContent>
          </Item>

          <Item>
            <ItemMedia>
              <ClockIcon className="text-primary" />
            </ItemMedia>
            <ItemContent>
              <span className="text-sm text-gray-600">
                Dernière modification
              </span>
              <ItemTitle>
                <AbsoluteDate date={lastModified} />
              </ItemTitle>
              <ItemDescription className={expiryClassName}>
                Expire dans {daysRemaining} jour{daysRemaining > 1 ? "s" : ""}
              </ItemDescription>
            </ItemContent>
          </Item>

          <Item>
            <ItemContent>
              <span className="text-sm text-gray-600">Progression</span>
              <FlexRow className="gap-2">
                <Progress value={(currentStep / steps) * 100} />
                <span className="text-sm whitespace-nowrap text-gray-600">
                  {currentStep} / {steps}
                </span>
              </FlexRow>
            </ItemContent>
          </Item>
        </div>
      </CardContent>
      <CardFooter>
        <FlexRow className="w-full">
          <span>
            Créé par <span className="font-semibold">{author}</span> le{" "}
            <AbsoluteDate date={createdAt} />
          </span>

          <ButtonGroup>
            <Button variant="outline" onClick={() => onEdit?.()}>
              <SquarePenIcon />
              Reprendre
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="text-destructive">
                  <Trash2Icon />
                  Supprimer
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Supprimer ce brouillon ?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Cette action est définitive. Le brouillon {name} sera
                    supprimé et ne pourra pas être restauré.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction
                    variant="destructive"
                    onClick={() => onDelete?.()}
                  >
                    Supprimer
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </ButtonGroup>
        </FlexRow>
      </CardFooter>
    </Card>
  );
}

export { DraftSummaryCard };
